import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { storage } from '@uhg-abyss/web/tools/storage';
import { SearchState } from '#/store/useSearchStore/searchStore';
import React, { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeStandardSearchEvent } from '../../../../common/AdobeTagging/adobeStandardSearchEvent';
import { CardWrapperDetailsPage } from '../../../../common/CardWrapper';
import {
  AdobeEventMethods,
  Constants,
  ProviderTypes,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { DetailsPageHeader } from '../../../../common/DetailsPageHeader/DetailsPageHeader';
import { LoadingSpinnerComp } from '../../../../common/Loader';
import { ProviderDetailTabs } from '../../../../common/ProviderDetailTabs/ProviderDetailTabs';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { SendFeedback } from '../../../../common/SendFeedback/SendFeedback';
import {
  getSearchLocation,
  internalConvertProviderTypeToAdobeType,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useInitializeStore } from '../../../../hooks/useInitializeStore';
import { useProviderDetails } from '../../../../hooks/useProviderDetails';
import { useProviderSearchTelemetry } from '../../../../hooks/useProviderSearchTelemetry';
import { ProviderType } from '../../../../models/ProviderDetails';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../../store/useAnalyticsStore/analyticsStore';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../../../store/useSearchStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import {
  getNetworkStatus,
  getSelectedLocationIndex,
} from '../../../../utils/providerDetails.utils';
import { getCurrentMember } from '../../../../utils/user.utils';
import { concatenateStrings, getTelemetryCoverageType } from '../MapView/utils';

const LOCATIONS_TAB_INDEX = 1;

export const GroupDetails = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const { coverageType, chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
    }))
  );

  const { searchTerm, searchFromTypeAhead, searchBySpeciality, searchType } =
    useTypeaheadStore(
      useShallow((state: TypeaheadState) => ({
        searchTerm: state.typeaheadSearchStore.searchTerm,
        searchFromTypeAhead: state.typeaheadSearchStore.searchFromTypeAhead,
        searchBySpeciality: state.typeaheadSearchStore.searchBySpeciality,
        searchType: state.typeaheadSearchStore.searchType,
      }))
    );

  const { avatar, showDirection, providerId } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      avatar: state.detailsState.avatar,
      showDirection: state.detailsState.showDirection,
      providerId: state.detailsState.providerId,
      locationId: state.detailsState.locationId,
    }))
  );

  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  const currentMember = getCurrentMember();

  const isProviderCardSelected =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION.IS_PROVIDER_CARD_SELECTED
    ) ?? false;

  const selectedProviderPrimarySpecialty = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_SPECIALTY
  );

  const [
    enableSendFeedback,
    enableNewFeedbackFormDesignEarlyPayFlag,
    enableNewFeedbackFormDesignFlag,
    isSearchRelevancyEventTrackingEnabled,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SUGGEST_AN_EDIT_FORM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_FEEDBACK_FORM_DESIGN_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_FEEDBACK_FORM_DESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD,
  ]);

  const enableNewFeedbackFormDesign = currentMember.isIFPEarlyPay
    ? enableNewFeedbackFormDesignEarlyPayFlag
    : enableNewFeedbackFormDesignFlag;

  const { providerId: storeProviderId, locationId: storeLocationId } =
    useInitializeStore(ConstantsRoutes.PROVIDER_GROUP_DETAILS.key);

  const [navigateToDirections, setNavigateToDirections] = useState(
    Boolean(showDirection)
  );
  const [isMapView, setIsMapView] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(
    navigateToDirections ? LOCATIONS_TAB_INDEX : 0
  );
  const { setShowMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
  }));
  const storedLocation = getGeoLocationFromStorage();
  const { data, isLoading } = useProviderDetails({
    providerType: ProviderType.MEDICAL_GROUP,
    providerId: storeProviderId,
    locationId: storeLocationId,
  });

  useEffect(() => {
    if (
      data?.facilityDetails?.providerDetails?.providerName &&
      searchFromTypeAhead &&
      !searchBySpeciality
    ) {
      adobeStandardSearchEvent({
        linkName,
        term: searchTerm,
        numberOfResults: 1,
        method: AdobeEventMethods.Predictive,
        customAttributesBlock: {
          searchLocation: getSearchLocation(storedLocation),
          providerType: internalConvertProviderTypeToAdobeType(
            ProviderTypes.MEDICALGROUP
          ),
        },
      });
    }
  }, [
    data?.facilityDetails?.providerDetails?.providerName,
    searchFromTypeAhead,
    searchBySpeciality,
  ]);

  useEffect(() => {
    if (showDirection) {
      setNavigateToDirections(true);
    }
  }, [showDirection]);

  useEffect(() => {
    if (navigateToDirections) {
      setActiveTab(LOCATIONS_TAB_INDEX);
    }
  }, [navigateToDirections]);

  const handleTabChange = (index) => {
    setShowMaximumProviderLimitBanner(false);
    if (index !== LOCATIONS_TAB_INDEX) {
      setNavigateToDirections(false);
    }
    adobeLinkTrackEvent({
      name: Object.values(Constants.PROVIDER_GROUP_DETAILS.TABS_LIST)[index],
      location: `body:details tabs`,
      type: 'tab',
    });
    setActiveTab(index);
  };

  const providerGroupDetails = data?.facilityDetails?.providerDetails || {};
  const providerLocations = providerGroupDetails?.providerLocations || [];
  let providerSpecialties = providerGroupDetails?.specialties;

  if (isProviderCardSelected) {
    providerSpecialties = [selectedProviderPrimarySpecialty];
  }

  const [selectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );

  const selectedLocationIndex = getSelectedLocationIndex(
    providerLocations,
    selectedId
  );
  const selectedLocation = providerLocations?.[selectedLocationIndex];

  const networkStatus = getNetworkStatus(selectedLocation?.network?.status);

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [currentPageDetails] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CURRENT_PAGE_DETAILS,
    {}
  );

  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  const { longitude, latitude, name } = getGeoLocationFromStorage();
  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );

  const hitProviderSearchTelemetry = async () => {
    const providerList = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST
    );
    const selectedProviderIndex =
      storage.session.get(
        Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_INDEX
      ) + 1;

    const filteredResults = providerList
      .map((item) => {
        if (!item) return null; // Skip invalid items

        const providerType = item?.providerType?.toLowerCase();
        const address = item?.displayAddress
          ? item?.displayAddress
          : concatenateStrings(
              item?.address?.line?.join('') || '',
              item?.address?.city || '',
              item?.address?.district || '',
              item?.address?.state || '',
              item?.address?.postalCode || ''
            );

        return {
          resultId: item?.providerId || '',
          resultType:
            providerType === ProviderTypes?.PRACTITIONER
              ? 'PROVIDER'
              : providerType === ProviderTypes.MEDICAL_GROUP
              ? 'GROUP'
              : 'FACILITY',
          name: item?.providerName || item?.displayName || '',
          specialty: item?.speciality?.[0]?.category || item?.speciality || '',
          address,
          coverageType: Array.isArray(coverageType)
            ? coverageType?.map(getTelemetryCoverageType)
            : getTelemetryCoverageType(coverageType),
          rating: item?.healthGradeRating
            ? parseFloat(item?.healthGradeRating)
            : 0,
          numRatings: item?.surveyCount || 0,
          inNetworkLabel: item?.networkStatus || '',
          costIndicationLabel: item?.costIndicator || '',
        };
      })
      .filter(Boolean);
    const telemetryVariables = {
      correlationId: correlationIDSessionStorage,
      searchPlatform: 'web',
      timestamp: new Date().toISOString(),
      searchQuery: searchTerm ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: searchRadius ?? 0,
      },
      interpretedAsNameSearch: searchType !== 'speciality',
      viewType: 'other',
      results: filteredResults,
      resultPage: currentPageDetails?.pageNumber || 1,
      pageSize: providerList?.length,
      resultedInNullSearch: filteredResults?.length === 0,
      selectedResult: {
        resultId: providerGroupDetails?.providerId,
        resultType:
          providerGroupDetails?.providerType?.toLowerCase() ===
          ProviderTypes?.PRACTITIONER
            ? 'PROVIDER'
            : providerGroupDetails?.providerType?.toLowerCase() ===
              ProviderTypes.MEDICAL_GROUP
            ? 'GROUP'
            : 'FACILITY',
        name: providerGroupDetails?.providerName,
        specialty:
          Array.isArray(providerList[selectedProviderIndex - 1]?.speciality) &&
          providerList[selectedProviderIndex - 1]?.speciality[0]?.category
            ? providerList[selectedProviderIndex - 1]?.speciality[0]?.category
            : providerList[selectedProviderIndex - 1]?.speciality || '',
        address: concatenateStrings(
          providerGroupDetails?.providerLocations[0]?.address?.line?.join(''),
          providerGroupDetails?.providerLocations[0]?.address?.city,
          providerGroupDetails?.providerLocations[0]?.address?.district,
          providerGroupDetails?.providerLocations[0]?.address?.state,
          providerGroupDetails?.providerLocations[0]?.address?.postalCode
        ),
        coverageType: Array.isArray(coverageType)
          ? coverageType?.map(getTelemetryCoverageType)
          : getTelemetryCoverageType(coverageType),
        rating:
          providerGroupDetails?.healthGradeRating &&
          parseFloat(providerGroupDetails?.healthGradeRating),
        numRatings: providerGroupDetails?.surveyCount,
        inNetworkLabel: providerList[selectedProviderIndex - 1]?.networkStatus,
        costIndicationLabel:
          providerGroupDetails?.providerLocations[0]?.costIndicator,
      },
      selectedResultOverallRank: selectedProviderIndex
        ? currentPageDetails?.pageNumber > 1
          ? (currentPageDetails?.pageNumber - 1) *
              currentPageDetails?.pageSize +
            selectedProviderIndex
          : selectedProviderIndex
        : 1,
      selectedResultOnCurrentPageRank: selectedProviderIndex,
      event: 'detail',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    if (
      providerGroupDetails?.providerName &&
      !isLoading &&
      searchTerm &&
      searchTerm?.length > 0 &&
      isSearchRelevancyEventTrackingEnabled
    ) {
      hitProviderSearchTelemetry();
    }
  }, [providerGroupDetails?.providerName, isLoading, searchTerm]);

  const { dependentSeqNbr } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingSpinnerComp isLoading={isLoading} />
      ) : (
        <React.Fragment>
          {!(isMapView && mobileScreen) ? (
            <CardWrapperDetailsPage backgroundColor="#FBFCFE">
              <DetailsPageHeader
                avatar={avatar}
                coverageType={coverageType}
                dependentSeqNbr={dependentSeqNbr}
                enableNewFeedbackFormDesign={enableNewFeedbackFormDesign}
                enableSendFeedback={enableSendFeedback}
                feedbackFormType={
                  Constants.FEEDBACK_FORM_TYPES.PROVIDER_GROUP_DETAILS
                }
                formProviderType={providerGroupDetails?.providerType}
                handleTabChange={handleTabChange}
                imageLocation={providerGroupDetails?.imageLocation}
                inNetwork={networkStatus}
                networkId={providerGroupDetails?.networkId}
                npi={providerGroupDetails?.npi}
                primarySpeciality={providerSpecialties}
                professionalDesignation={undefined}
                providerDetails={providerGroupDetails}
                providerId={providerId}
                providerLocations={providerLocations}
                providerName={providerGroupDetails?.providerName}
                providerType={ProviderType.PROVIDER_GROUP}
                sectionTypeValue={Constants.RESULT_SECTION.PROVIDER_GROUPS}
                setNavigateToDirections={setNavigateToDirections}
                virtualCareOffered={selectedLocation?.virtualCareOffered}
              />
            </CardWrapperDetailsPage>
          ) : null}
          <CardWrapperDetailsPage>
            <ProviderDetailTabs
              active={activeTab}
              chipValue={chipValue}
              coverageType={coverageType}
              handleTabChange={handleTabChange}
              isMapView={isMapView}
              navigateToDirections={navigateToDirections}
              providerDetails={providerGroupDetails}
              setIsMapView={setIsMapView}
              setNavigateToDirections={setNavigateToDirections}
            />
          </CardWrapperDetailsPage>
        </React.Fragment>
      )}
      {enableSendFeedback && !enableNewFeedbackFormDesign && (
        <SendFeedback
          coverageType={coverageType}
          data={{
            name: providerGroupDetails?.providerName,
            address: selectedLocation?.address,
            npi: providerGroupDetails?.npi,
            mpin: providerGroupDetails?.providerId,
            providerType: providerGroupDetails?.providerType,
          }}
          type={Constants.FEEDBACK_FORM_TYPES.PROVIDER_GROUP_DETAILS}
        />
      )}
    </React.Fragment>
  );
};
